import React, { memo } from 'react';
import { Box, Button, Alert } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMonthlyShiftSlotManager } from '../hooks/useMonthlyShiftSlotManager';
import type { MonthlyShiftSlotSummaryRepository } from '../../../../application/monthlyShiftSlot/ports/MonthlyShiftSlotSummaryRepository';
import MonthlyShiftSlotTable from '../components/MonthlyShiftSlotTable';
import MonthlyShiftSlotDeleteDialog from '../components/MonthlyShiftSlotDeleteDialog';
import {
  HeaderSection,
  ActionArea,
  TableSection,
  ErrorContainer
} from '../MonthlyShiftSlotManager.styles';

/**
 * MonthlyShiftSlotManagerContainer のProps型
 *
 * @property {MonthlyShiftSlotSummaryRepository} repository - DIされるRepository実装
 */
interface Props {
  repository: MonthlyShiftSlotSummaryRepository;
}

/**
 * 月次シフト枠管理コンテナコンポーネント
 *
 * @description
 * ビジネスロジックと状態管理を担当するコンテナコンポーネント。
 * Custom Hookを使用してデータ取得・削除処理を行い、
 * 子コンポーネントにデータとハンドラーを渡す。
 *
 * @param {Props} props - コンポーネントProps
 * @returns {React.ReactElement} コンテナコンポーネント
 */
const MonthlyShiftSlotManagerContainer: React.FC<Props> = memo(({ repository }) => {
  const navigate = useNavigate();
  const {
    data,
    meta,
    isLoading,
    error,
    deleteDialogOpen,
    selectedSummary,
    handlePageChange,
    showDeleteDialog,
    closeDeleteDialog,
    deleteSummary,
    clearError
  } = useMonthlyShiftSlotManager(repository);

  const handleEdit = (year: number, month: number) => {
    navigate(`/doctors/monthly-shift-slots?year=${year}&month=${month}`);
  };

  const handleEditShiftSlot = (year: number, month: number) => {
    navigate(`/doctors/shifts/monthly?year=${year}&month=${month}`);
  };

  const handleCreateNew = () => {
    navigate('/doctors/monthly-shift-slots');
  };

  return (
    <Box>
      {/* ヘッダー */}
      <HeaderSection>
        <ActionArea>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            月次シフト新規作成
          </Button>
        </ActionArea>
      </HeaderSection>

      {/* エラー表示 */}
      {error && (
        <ErrorContainer>
          <Alert severity="error" onClose={clearError}>
            {error}
          </Alert>
        </ErrorContainer>
      )}

      {/* テーブル */}
      <TableSection>
        <MonthlyShiftSlotTable
          data={data}
          isLoading={isLoading}
          meta={meta}
          onPageChange={handlePageChange}
          onEdit={handleEdit}
          onEditShiftSlot={handleEditShiftSlot}
          onDelete={showDeleteDialog}
        />
      </TableSection>

      {/* 削除確認ダイアログ */}
      <MonthlyShiftSlotDeleteDialog
        open={deleteDialogOpen}
        summary={selectedSummary}
        onClose={closeDeleteDialog}
        onConfirm={deleteSummary}
        isLoading={isLoading}
      />
    </Box>
  );
});

MonthlyShiftSlotManagerContainer.displayName = 'MonthlyShiftSlotManagerContainer';

export default MonthlyShiftSlotManagerContainer;
